import React, {useEffect, useMemo} from 'react'
import styles from './UIGameTimer.module.scss'
import clsx from 'clsx'
import {Timer} from '../../common/Timer/Timer'
import {lerp} from '@colonist/utils'
import {Sound} from '../../utils/sound'
import {useGameTimer} from '../../hooks/useGameTimer'
import {useGameStore} from '../../store/GameStoreContext'
import {ModeSettingEnum} from '@colonist/types'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator'
import {ActionState} from '@colonist/game-states'

export interface UIGameTimerProps {
    timeLeftInSeconds: number
    isMyPlayerTakingAction: boolean
    isPaused: boolean
    timerStyle?: 'white' | 'beige'
    className?: string | undefined
}

export function UIGameTimer({
    timeLeftInSeconds,
    isMyPlayerTakingAction,
    isPaused,
    timerStyle = 'beige',
    className,
}: UIGameTimerProps) {
    const isBeginnerMode = useGameStore((state) => state.gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P)
    const liveTimeLeftInSeconds = useGameTimer({
        timeLeftInSeconds: timeLeftInSeconds,
        isPaused: isPaused,
    })

    const SECONDS_LEFT_BEFORE_TICKING_SOUND = 5
    const currentStateValidator = useCurrentStateValidator()
    const myColor = useGameStore((state) => state.myColor)
    const shouldPlayTickingSound = currentStateValidator.currentTurnPlayers.length == 1
        || currentStateValidator.getPlayerActionState(myColor) != ActionState.None

    const isPlayerTakingActionAtTheEndOfTheirTurn = useMemo(() => {
        const isRed = isMyPlayerTakingAction && liveTimeLeftInSeconds <= SECONDS_LEFT_BEFORE_TICKING_SOUND
        return isRed
    }, [isMyPlayerTakingAction, liveTimeLeftInSeconds])

    const playTickingSound = (secondsLeft: number) => {
        if(!isMyPlayerTakingAction) return
        if(!shouldPlayTickingSound) return
        if(secondsLeft > SECONDS_LEFT_BEFORE_TICKING_SOUND) return

        const isFirstReminder = secondsLeft == SECONDS_LEFT_BEFORE_TICKING_SOUND
        if(isFirstReminder) Sound.playSound(Sound.SoundType.FirstReminder, 0.6, 1.0)

        const progress = (SECONDS_LEFT_BEFORE_TICKING_SOUND - secondsLeft) / SECONDS_LEFT_BEFORE_TICKING_SOUND

        const minimumVolume = 1.0
        const maximumVolume = 1.2
        const volume = lerp(minimumVolume, maximumVolume, progress)

        const minimumPitch = 1.0
        const maximumPitch = 1.5
        const pitch = lerp(minimumPitch, maximumPitch, progress)

        Sound.playSound(Sound.SoundType.ClockTick, volume, pitch)
    }

    useEffect(() => {
        playTickingSound(liveTimeLeftInSeconds)
    }, [liveTimeLeftInSeconds])

    return <div className={clsx(styles.container, timerStyle === 'beige' && styles.beigeBackground, className, {
        [styles.hidden]: isBeginnerMode,
    })}>
        <Timer
            className={clsx({
                [styles.red]: isPlayerTakingActionAtTheEndOfTheirTurn,
                [styles.timerWhite]: timerStyle === 'white',
                [styles.timerBeige]: timerStyle === 'beige'})}
            timeLeftInSeconds={liveTimeLeftInSeconds}
        />
    </div>
}
